import type { PlaneSize } from '@Components/BigBrowse/Model/ModelTypes';
import { calculateFillScaleFactor } from '@Utils/scale-utils';
import { THREE } from 'expo-three';

/** Makes a THREE.texture Image to fill its wrapping plane. If no source (Image Bitmap) size is provided,
 * then it is automatically derived from 'texture' based on its 'width,height' properties.
 * */
export function makeModeFillImage({
  planeSize,
  tex,
  sourceSize,
}: {
  planeSize: PlaneSize;
  tex: THREE.Texture;
  sourceSize?: PlaneSize;
}): void {
  makeModeFill({
    planeSize,
    tex,
    sourceSize,
    sourceSizeExtraction: ['width', 'height'],
  });
}

type MakeModeFillVideoParams = {
  planeSize: PlaneSize;
  tex: THREE.Texture;
  sourceSize?: PlaneSize;
};

/** Makes a Three Video texture to 'Fill' a container rectangle while keeping aspect ratio. If no source (Video Bitmap) size
 * is provided, then it is automatically derived from the texture's 'videoHeight', 'videoWidth' props. */
export function makeModeFillVideo({
  planeSize,
  tex,
  sourceSize,
}: MakeModeFillVideoParams): void {
  makeModeFill({
    planeSize,
    tex,
    sourceSize,
    sourceSizeExtraction: ['videoWidth', 'videoHeight'],
  });
}

/** Makes a Three Image texture to 'Fill' a container rectangle while keeping aspect ratio. It needs the source size, or the props of the texture
 * from which to derive the source size ('videoWidth', 'videoHeight' or 'width', 'height').
 * */

type MakeModeFillParams = {
  planeSize: PlaneSize;
  tex: THREE.Texture;
  sourceSize?: PlaneSize;
  sourceSizeExtraction: ['videoWidth' | 'width', 'height' | 'videoHeight'];
};

export function makeModeFill({
  planeSize,
  tex,
  sourceSize,
  sourceSizeExtraction,
}: MakeModeFillParams): void {
  const effectiveSourceSize: PlaneSize = {
    width: sourceSize?.width ?? tex.image[sourceSizeExtraction[0]],
    height: sourceSize?.height ?? tex.image[sourceSizeExtraction[1]],
  };

  const scaleFactor = calculateFillScaleFactor({
    containerWidth: planeSize.width,
    containerHeight: planeSize.height,
    containedWidth: effectiveSourceSize.width,
    containedHeight: effectiveSourceSize.height,
  });

  const scaledImageSize = {
    width: scaleFactor * effectiveSourceSize.width,
    height: scaleFactor * effectiveSourceSize.height,
  };
  const repeatX = planeSize.width / scaledImageSize.width;
  const repeatY = planeSize.height / scaledImageSize.height;

  tex.repeat.set(repeatX, repeatY);
  tex.offset.set((1 - repeatX) / 2, (1 - repeatY) / 2);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
}
